import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormControlLabel, Typography } from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ConfirmationMessageModal,
  FullScreenLoader,
  Modal,
  ModalAlert,
  ScrollToTop,
  TextFieldWrapper,
  ToggleSwitch,
} from '@Components/index';
import CommonConstants from '@Constants/common.constants';
import {
  GroupsModalInputs,
  GroupsModalProps,
} from '@Containers/Groups/GroupsModal/GroupsModal.types';
import GroupsModalSchema from '@Containers/Groups/GroupsModal/GroupsModalSchema';
import { GroupAdd, GroupEdit } from '@Models/groups.models';
import {
  useAddGroupMutation,
  useDeleteGroupMutation,
  useEditGroupMutation,
  useGetGroupQuery,
} from '@Services/groups.service';
import {
  BaseAlertProps,
  CommonApiError,
  ConfirmationActionTypes,
  DialerCrudTypes,
} from '@Typings/global.types';
import { getConfirmationMessageNode } from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const GroupsModal: FunctionComponent<GroupsModalProps> = (
  props: GroupsModalProps
): JSX.Element => {
  const { groupId, setGroupId, crudType, setCrudType } = props;

  const { t } = useTranslation();
  const methods = useForm<GroupsModalInputs>({
    resolver: yupResolver(GroupsModalSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });
  const { triggerAlert } = useAlert();

  const {
    data: groupData,
    isLoading: groupIsLoading,
    isFetching: groupIsFetching,
    error: getGroupError,
  } = useGetGroupQuery(groupId, {
    skip: !groupId || !crudType,
  });
  const [addGroup, addGroupMethods] = useAddGroupMutation();
  const [editGroup, editGroupMethods] = useEditGroupMutation();
  const [deleteGroup, deleteGroupMethods] = useDeleteGroupMutation();

  const loading =
    groupIsLoading ||
    groupIsFetching ||
    addGroupMethods.isLoading ||
    editGroupMethods.isLoading ||
    deleteGroupMethods.isLoading;

  const readOnly = crudType === DialerCrudTypes.View;

  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [modalAlertState, setModalAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);

  const onSubmit: SubmitHandler<GroupsModalInputs> = (formData) => {
    const newData: GroupAdd | GroupEdit = {
      name: formData.name,
      isPublic,
    };
    if (groupData && crudType === DialerCrudTypes.Edit) {
      return editGroup({ ...groupData, ...newData });
    }
    if (crudType === DialerCrudTypes.Add) {
      return addGroup(newData);
    }
  };

  const handleClose = useCallback(() => {
    setCrudType(null);
    setGroupId('');
    setModalAlertState({ openAlert: false });
    methods.unregister();
  }, [methods, setCrudType, setGroupId]);

  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      handleClose();
      return deleteGroup(groupId);
    }
  };

  const getHeaderText = (): string | undefined => {
    if (loading) {
      return '';
    }

    return crudType === DialerCrudTypes.Add
      ? t('groups.New Group')
      : groupData?.name;
  };

  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => setIsPublic(checked);

  useEffect(() => {
    if (getGroupError) {
      handleClose();
      triggerAlert(getGroupError, setAlertState);
    }
  }, [getGroupError, handleClose, triggerAlert]);

  useEffect(() => {
    if (addGroupMethods.error) {
      if ((addGroupMethods.error as FetchBaseQueryError).status === 400) {
        methods.setValue('name', '');
      }
      triggerAlert(addGroupMethods.error, setModalAlertState);
    }

    if (addGroupMethods.isSuccess) {
      triggerAlert(CommonConstants.GLOBAL_MESSAGES.ADD_GROUP, setAlertState);

      handleClose();
    }
  }, [
    methods,
    addGroupMethods.data,
    addGroupMethods.isSuccess,
    addGroupMethods.error,
    handleClose,
    triggerAlert,
  ]);

  useEffect(() => {
    if (editGroupMethods.error) {
      if (
        ((editGroupMethods.error as FetchBaseQueryError).data as CommonApiError)
          .message !== CommonConstants.GLOBAL_ERROR_CODES.RESOURCE_IS_A_DEFAULT
      ) {
        triggerAlert(editGroupMethods.error, setModalAlertState);
      }
    }

    if (editGroupMethods.isSuccess) {
      triggerAlert(CommonConstants.GLOBAL_MESSAGES.EDIT_GROUP, setAlertState);

      handleClose();
    }
  }, [
    editGroupMethods.data,
    editGroupMethods.isSuccess,
    editGroupMethods.error,
    handleClose,
    triggerAlert,
  ]);

  useEffect(() => {
    if (groupData && crudType !== DialerCrudTypes.Add) {
      setIsPublic(groupData.isPublic);
      methods.reset({
        name: groupData.name,
      });
    } else if (crudType === DialerCrudTypes.Add) {
      setIsPublic(false);
      methods.reset({
        name: '',
      });
    }
  }, [methods, groupData, crudType]);

  const getButtonGroupComponent = (): JSX.Element | null => {
    switch (crudType) {
      case DialerCrudTypes.Add:
        return (
          <>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              type={methods.formState.isValid ? 'button' : 'submit'}
              variant="contained"
              color="primary"
              onClick={() => {
                if (methods.formState.isValid) {
                  // setIsConfirmationModalOpen(true);
                  setConfirmationActionType(ConfirmationActionTypes.Save);
                  onSubmit(methods.getValues());
                }
              }}
            >
              {t('common.messages.Save')}
            </Button>
          </>
        );
      case DialerCrudTypes.View:
        return (
          <>
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setIsConfirmationModalOpen(true);
                  setConfirmationActionType(ConfirmationActionTypes.Delete);
                }}
              >
                {t('common.messages.Delete')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ ml: 'auto', mr: 1.6 }}
                disabled={!methods.formState.isValid}
                onClick={() => setCrudType(DialerCrudTypes.Edit)}
              >
                {t('common.messages.Edit')}
              </Button>
            </>
          </>
        );
      case DialerCrudTypes.Edit:
        return (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setIsConfirmationModalOpen(true);
                setConfirmationActionType(ConfirmationActionTypes.Delete);
              }}
            >
              {t('common.messages.Delete')}
            </Button>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              type={methods.formState.isValid ? 'button' : 'submit'}
              variant="contained"
              color="primary"
              disabled={!methods.formState.isValid}
              onClick={() => {
                if (methods.formState.isValid) {
                  setConfirmationActionType(ConfirmationActionTypes.Save);
                  onSubmit(methods.getValues());
                }
              }}
            >
              {t('common.messages.Save')}
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(confirmationActionType)}
        onClick={handleConfirmationModalClick}
      />
      <Modal
        headerText={getHeaderText()}
        open={Boolean(crudType)}
        onClose={handleClose}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          <Box
            className="group-modal-form"
            component="form"
            role="form"
            onSubmit={methods.handleSubmit(onSubmit)}
            sx={{
              position: 'relative',
              maxHeight: '70vh',
              padding: '3rem 2.4rem 2.4rem',
              overflow: loading ? 'hidden' : 'auto',
            }}
          >
            <ScrollToTop
              className="script-modal-form"
              dependencies={[loading]}
            />
            {loading ? (
              <FullScreenLoader
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'common.white',
                  zIndex: 101,
                }}
              />
            ) : (
              <ModalAlert alertState={modalAlertState} />
            )}
            <TextFieldWrapper
              name="name"
              variant="outlined"
              label={`${t('common.messages.Name')} *`}
              placeholder={t('common.messages.Group Name')}
              sx={{
                minWidth: '51.2rem',
                mt: modalAlertState.openAlert ? 7 : undefined,
              }}
              InputProps={{
                readOnly,
              }}
            />
            <Box display="flex" mt={3.8}>
              <Box>
                <Typography variant="main">
                  {t('common.table.Public')}
                </Typography>
                <FormControlLabel
                  control={
                    <ToggleSwitch
                      checked={isPublic}
                      readOnly={readOnly}
                      onChange={readOnly ? () => {} : handleToggleChange}
                    />
                  }
                  label={
                    <Typography variant="main">
                      {t(
                        isPublic ? 'common.messages.Yes' : 'common.messages.No'
                      )}
                    </Typography>
                  }
                />
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              {getButtonGroupComponent()}
            </Box>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};

export default GroupsModal;
